//===----------------------------------------------------------------------===//
//
//  Copyright 2024-2026 Trovares Inc. dba Rocketgraph.  All rights reserved.
//
//===----------------------------------------------------------------------===//

import AltRouteIcon from '@mui/icons-material/AltRoute';
import AssessmentIcon from '@mui/icons-material/Assessment';
import BiotechIcon from '@mui/icons-material/Biotech';
import BugReportIcon from '@mui/icons-material/BugReport';
import ExtensionIcon from '@mui/icons-material/Extension';
import HubIcon from '@mui/icons-material/Hub';
import InsightsIcon from '@mui/icons-material/Insights';
import MonitorHeartIcon from '@mui/icons-material/MonitorHeart';
import PolylineIcon from '@mui/icons-material/Polyline';
import QueryStatsIcon from '@mui/icons-material/QueryStats';
import ScienceIcon from '@mui/icons-material/Science';
import SpeedIcon from '@mui/icons-material/Speed';
import TerminalIcon from '@mui/icons-material/Terminal';
import TimelineIcon from '@mui/icons-material/Timeline';
import TravelExploreIcon from '@mui/icons-material/TravelExplore';
import TuneIcon from '@mui/icons-material/Tune';

// The names a plugin manifest may ask for, and what each one draws.
//
// An allowlist rather than a lookup into `@mui/icons-material`: a manifest
// cannot hand React a component, and resolving an arbitrary string against
// that package would be a dynamic-import surface steered by a plugin. The
// vocabulary is the MUI module basename, so `MonitorHeart` is the icon at
// `@mui/icons-material/MonitorHeart` -- an existing name a manifest author
// can look up rather than one invented here.
//
// Icons already spoken for by the header's own nav -- Dashboard, Explore,
// Storage, AccountTree, Work, UploadFile -- and by the Ask AI launcher
// (`chatPanelIcon` in `@/components/Chat/chatPanelId`) are deliberately
// absent. A collision costs most in the icon-only overflow tier, where the
// glyph is the whole of a pill's identity.
const PANEL_ICONS: Readonly<Record<string, React.ReactNode>> = {
  AltRoute: <AltRouteIcon />,
  Assessment: <AssessmentIcon />,
  Biotech: <BiotechIcon />,
  BugReport: <BugReportIcon />,
  Extension: <ExtensionIcon />,
  Hub: <HubIcon />,
  Insights: <InsightsIcon />,
  MonitorHeart: <MonitorHeartIcon />,
  Polyline: <PolylineIcon />,
  QueryStats: <QueryStatsIcon />,
  Science: <ScienceIcon />,
  Speed: <SpeedIcon />,
  Terminal: <TerminalIcon />,
  Timeline: <TimelineIcon />,
  TravelExplore: <TravelExploreIcon />,
  Tune: <TuneIcon />
};

// What an unrecognized name draws: the same glyph the plugins menu already
// uses for a surface, so an unresolved icon still reads as "a plugin".
export const FALLBACK_PANEL_ICON = 'Extension';

/** Every name a manifest may use, sorted, for documentation and validation. */
export const panelIconNames = (): string[] =>
  Object.keys(PANEL_ICONS).sort();

/**
 * Resolve a manifest's icon name to something renderable.
 *
 * An unknown name falls back rather than throwing, which is the one place
 * this feature departs from the loud-failure stance elsewhere: a wrong glyph
 * is cosmetic, while a throw here would take down the whole plugins menu over
 * a typo in a field that decorates. A name that is not a string at all is a
 * structural violation and is the manifest validator's to reject.
 *
 * Own properties only: the table is an object literal, so a plugin asking for
 * `constructor` or `toString` would otherwise be handed something off
 * `Object.prototype` that React cannot render.
 */
export const resolvePanelIcon = (name: string): React.ReactNode =>
  isKnownPanelIcon(name) ?
    PANEL_ICONS[name] : PANEL_ICONS[FALLBACK_PANEL_ICON];

/** Whether a name resolves to its own icon rather than to the fallback. */
export const isKnownPanelIcon = (name: string): boolean =>
  Object.prototype.hasOwnProperty.call(PANEL_ICONS, name);
